/**
 * Offene Ja/Nein-Karte (22.10.2026, Martin 07:55: „hier steht schon wieder Ja in einem Text … obwohl oben das Ja schon bestätigt ist …
 * und darum ist das Mikro aufgegangen, wenn ich mit einem Ja nur auf dem System hier antworten kann“).
 *
 * Eine Ja/Nein-Frage mit Kennung (Karte `k-…`, lib/frage-knoepfe.ts) beantwortet NUR der Knopf. Darum:
 *  - Fragt ein Entwickler per Karte, öffnet das Auto-Mikro nach seinem Vorlesen nicht (`frageIstKarte`) — die Antwort ist der Knopf,
 *    auch nach dem Tipp öffnet nichts von selbst (der Vorgang ist abgeschlossen). Steht irgendeine Karte noch OFFEN (`offeneJaKarte`),
 *    öffnet auch die Frage eines anderen Entwicklers das Mikro nicht von selbst. Martins eigener Druck auf das Mikro bleibt immer möglich.
 *  - Ein gesprochener Satz, der nur aus einem bloßen Antwortwort besteht (`nacktesAntwortWort`: „Ja“, „Nein“, „Ja bitte“, „Okay“,
 *    „Mach das“ …), geht bei offener Karte nicht als Chat-Nachricht hinaus — nur ein Hinweis (`JA_KARTE_HINWEIS`).
 *
 * ⚠️ Nur Anzeige- und Mikro-Seite. Das Ja-Tor (Kennung, Karten-Route, Knopf-Ja am Server, `bestaetigung-fremdtext`) bleibt unberührt;
 *    nichts hier macht aus Gesprochenem je ein Knopf-Ja — es wird höchstens NICHT gesendet.
 * ⚠️ Fail-safe: fehlt die Zeit an der Blase, gilt die Karte als offen (lieber Mikro zu als ein Ja-Satz zu viel).
 */
import { KARTE_HOECHSTALTER_MS } from "../../shared/schmied-karte.js";
import { endetMitFrage } from "./frage-am-ende.js";

export const JA_KARTE_HINWEIS = "Zum Antworten bitte Ja oder Nein auf der Karte tippen.";

/** Je Zusatz-Entwickler ist höchstens EINE Karte offen — eine neuere ersetzt seine vorige (server/schmied-karten.ts). */
const ZUSATZ = new Set(["zimmermann", "hueter"]);

const KARTEN = ".bubble.entwickler > .entwickler-frage[data-karte]";

/** Die Blase des Beitrags trägt eine Ja/Nein-Karte mit Kennung — seine Frage beantwortet nur der Knopf. */
export function frageIstKarte(blase: ParentNode | null | undefined): boolean {
  return blase?.querySelector(":scope > .entwickler-frage[data-karte]") != null;
}

/** Alle noch offenen Karten im Chat (nicht beantwortet, nicht ersetzt, nicht älter als 24 h), in DOM-Reihenfolge (= Zeit). */
export function offeneJaKarten(chat: ParentNode, jetzt: number): HTMLElement[] {
  const alle = Array.from(chat.querySelectorAll<HTMLElement>(KARTEN));
  // Bei Zimmermann/Hüter zählt nur die jüngste Karte (DOM-Reihenfolge = Zeit); ihre älteren sind ersetzt.
  const juengste = new Map<string, HTMLElement>();
  for (const k of alle) {
    const von = k.parentElement?.dataset.entwickler ?? "";
    if (ZUSATZ.has(von)) juengste.set(von, k);
  }
  return alle.filter((k) => {
    const von = k.parentElement?.dataset.entwickler ?? "";
    if (ZUSATZ.has(von) && juengste.get(von) !== k) return false;
    return karteNochOffen(k, jetzt);
  });
}

/** Die erste noch offene Karte im Chat — sonst `null`. */
export function offeneJaKarte(chat: ParentNode, jetzt: number): HTMLElement | null {
  return offeneJaKarten(chat, jetzt)[0] ?? null;
}

/** Unbeantwortet und nicht älter als 24 h. Fail-safe: fehlt die Zeit, gilt sie als offen. (Ersetzt-Sein prüft `offeneJaKarten`.) */
function karteNochOffen(k: HTMLElement, jetzt: number): boolean {
  if (k.dataset.beantwortet === "1") return false;
  const ts = blasenZeit(k.parentElement);
  return ts === null || jetzt - ts <= KARTE_HOECHSTALTER_MS;
}

function blasenZeit(b: HTMLElement | null | undefined): number | null {
  const ts = Number(b?.dataset.ts ?? NaN);
  return Number.isFinite(ts) && ts > 0 ? ts : null;
}

/*
 * Prüfauflage A2 (22.10.2026): hält eine Karte das Mikro oder verwirft ein Ja, steht EINE Journalzeile (ohne Text) mit Alter in Minuten
 * und Zahl der offenen Karten — einmal je Karte und Art, damit eine „vergessene Karte“ auffällt, ohne je Aufruf zu spammen.
 */
export type JaKarteMelder = (zeile: string) => void;
const gemeldet = new Set<string>();

function meldeEinmal(melde: JaKarteMelder | undefined, art: "mikro" | "verworfen", karte: HTMLElement | null, chat: ParentNode, jetzt: number): void {
  if (!melde || !karte) return;
  const kennung = karte.dataset.karte ?? "?";
  const schluessel = `${art}:${kennung}`;
  if (gemeldet.has(schluessel)) return;
  gemeldet.add(schluessel);
  const ts = blasenZeit(karte.parentElement);
  const alter = ts === null ? "?" : String(Math.max(0, Math.floor((jetzt - ts) / 60_000)));
  melde(`[ja-karte] haelt art=${art} alter=${alter}min offen=${offeneJaKarten(chat, jetzt).length}`);
}

/** Toleranz zwischen Client-Uhr (blasenZeit) und Schwelle: im Zweifel „frisch“ (hält zu). */
export const FRISCH_TOLERANZ_MS = 5_000;

/**
 * Hält eine Karte das Auto-Mikro eines Entwicklers zu? `blase`: der fragende Beitrag (fehlt er, gilt der jüngste Beitrag von `id`).
 * „ja-karte“: dieser Beitrag fragt per Karte (offen oder schon getippt — der Knopf ist die Antwort); „ja-karte-offen“: irgendeine
 * Karte ist noch offen; `null`: nichts hält (dann gilt das alte Verhalten).
 *
 * `seit`: bei Vespera-Zügen der BEGINN des Zugs (nicht das Ende der Wiedergabe) — eine Karte, die während ihrer Antwort kam, ist frisch.
 * `nurEigene`: nur die Karte DES Entwicklers `id` und fremde Karten NEUER als `seit` halten zu; ältere liegengebliebene nicht (Vespera fragt Martin, 28.10.2026).
 *
 * Prüfauflage A3: ohne mitgegebene Blase zählt die Karte des jüngsten Beitrags nur, wenn er NACH `seit` (Beginn des Wartens) kam
 * oder seine Karte wirklich offen ist — eine alte, beantwortete Karte von gestern hält eine Vespera-Frage nicht zu. Fehlt die Zeit an
 * der Blase, gilt sie als neu (fail-safe: lieber zu).
 */
export function jaKarteHaeltMikro(
  chat: ParentNode,
  id: string,
  blase: ParentNode | null,
  jetzt: number,
  seit: number | null = null,
  melde?: JaKarteMelder,
  nurEigene = false,
): "ja-karte" | "ja-karte-offen" | null {
  const beitrag = blase ?? juengsterBeitragVon(chat, id);
  const eigene = beitrag?.querySelector<HTMLElement>(":scope > .entwickler-frage[data-karte]") ?? null;
  if (eigene && (blase !== null || karteGiltFuerWarten(eigene, seit, jetzt))) {
    meldeEinmal(melde, "mikro", eigene, chat, jetzt);
    return "ja-karte";
  }
  // 28.10.2026 (Karussell): eine Vespera-Frage AN MARTIN (nurEigene) wird von einer fremden, ÄLTEREN offenen Karte nicht zugehalten.
  // Eine frische fremde Karte (Blase NACH `seit`, Zeit fehlt = neu) hält weiter zu — Martin soll sie per Knopf beantworten.
  if (nurEigene) {
    if (seit === null) return null;
    const frische = offeneJaKarten(chat, jetzt).find((k) => {
      const ts = blasenZeit(k.parentElement);
      return ts === null || ts >= seit - FRISCH_TOLERANZ_MS;
    });
    if (!frische) return null;
    meldeEinmal(melde, "mikro", frische, chat, jetzt);
    return "ja-karte-offen";
  }
  const offen = offeneJaKarte(chat, jetzt);
  if (!offen) return null;
  meldeEinmal(melde, "mikro", offen, chat, jetzt);
  return "ja-karte-offen";
}

/** Die Karte des jüngsten Beitrags (ohne mitgegebene Blase): neu seit `seit` (oder ohne Zeit) oder noch offen. */
function karteGiltFuerWarten(karte: HTMLElement, seit: number | null, jetzt: number): boolean {
  if (karteNochOffen(karte, jetzt)) return true;
  if (seit === null) return true; // ohne Beginn des Wartens: wie bisher (lieber zu)
  const ts = blasenZeit(karte.parentElement);
  return ts === null || ts >= seit - FRISCH_TOLERANZ_MS;
}

function juengsterBeitragVon(chat: ParentNode, id: string): HTMLElement | null {
  const alle = Array.from(chat.querySelectorAll<HTMLElement>(".bubble.entwickler")).filter((b) => b.dataset.entwickler === id);
  return alle[alle.length - 1] ?? null;
}

/** Die Antwortwörter: mindestens eines davon muss im Satz stehen. */
const KERN = new Set([
  "ja", "jaa", "jo", "jep", "jup", "yes", "jawohl", "nein", "nee", "ne", "nö", "noe", "no",
  "okay", "ok", "okey", "oke", "genau", "klar", "gerne", "gern", "passt", "einverstanden", "ordnung",
  "mach", "machs", "freilich", "sicher", "natürlich", "richtig", "korrekt", "logo",
]);
/** Füllwörter und Anrede, die ein bloßes Antwortwort begleiten dürfen. */
const BEIWERK = new Set([
  "bitte", "danke", "das", "es", "doch", "schon", "ruhig", "los", "gut", "super", "alles", "in", "auf", "jeden", "fall",
  "nicht", "sehr", "mal", "hey", "vespera", "zimmermann", "schmied", "hüter", "hueter",
]);
/** Länger ist kein bloßes Antwortwort mehr. */
const HOECHSTENS_WOERTER = 5;

/** Besteht der gesprochene Satz nur aus einem bloßen Ja/Nein-Wort (mit Füllwort/Anrede)? „Ja, mach die Mail fertig“ → nein. */
export function nacktesAntwortWort(text: string): boolean {
  const woerter = text
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\s]+/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (woerter.length === 0 || woerter.length > HOECHSTENS_WOERTER) return false;
  if (!woerter.every((w) => KERN.has(w) || BEIWERK.has(w))) return false;
  return woerter.some((w) => KERN.has(w));
}

/**
 * Prüfauflage A1: steht NACH der jüngsten offenen Karte eine neuere fragende Blase von Vespera oder einem Entwickler (endet mit einer
 * Frage, oder Ja/Nein-Knöpfe OHNE Kennung), galt das bloße Ja vermutlich dieser neueren Frage — dann hält die Karte es nicht zurück.
 * Eine neuere Karte (mit Kennung) gibt nichts frei: auch ihre Antwort ist der Knopf.
 */
function neuereFrageNach(karte: HTMLElement): boolean {
  const kartenBlase = karte.parentElement;
  if (!kartenBlase) return false;
  for (let b = kartenBlase.nextElementSibling; b; b = b.nextElementSibling) {
    if (!(b instanceof HTMLElement) || !b.classList.contains("bubble")) continue;
    const entw = b.classList.contains("entwickler");
    if (!entw && !b.classList.contains("bot")) continue;
    if (entw && b.querySelector(":scope > .entwickler-frage[data-karte]")) continue;
    if (entw && b.querySelector(":scope > .entwickler-frage")) return true;
    const text = b.querySelector(":scope > .bubble-content")?.textContent ?? "";
    if (endetMitFrage(text)) return true;
  }
  return false;
}

/**
 * Soll ein gesprochener Satz wegen einer offenen Karte NICHT gesendet werden? Nur bei nacktem Antwortwort UND offener Karte UND ohne
 * neuere fragende Blase nach der jüngsten offenen Karte (A1).
 */
export function gesprochenesJaVerwerfen(text: string, chat: ParentNode, jetzt: number, melde?: JaKarteMelder): boolean {
  if (!nacktesAntwortWort(text)) return false;
  const offen = offeneJaKarten(chat, jetzt);
  const juengste = offen[offen.length - 1];
  if (!juengste || neuereFrageNach(juengste)) return false;
  meldeEinmal(melde, "verworfen", juengste, chat, jetzt);
  return true;
}
